/**
 * Fusion et réconciliation.
 *
 * # Le modèle en une phrase
 *
 *   état affiché = projection(état serveur, opérations encore en attente)
 *
 * Le client ne fusionne pas champ par champ en comparant des horodatages. Il
 * **rejoue ses opérations non acquittées par-dessus l'état serveur** (rebase).
 * Il n'y a donc qu'une seule source de vérité — le serveur — et une seule pile
 * de modifications locales — la file.
 *
 * Conséquence pratique : dès qu'une opération est acquittée, elle sort de la
 * file, et elle disparaît de la projection à l'instant même où le serveur la
 * reflète. Aucun scintillement, aucun retour en arrière visuel.
 *
 * Ce module est pur : c'est lui qui rend testable, en mémoire et en quelques
 * millisecondes, le scénario des deux appareils hors ligne.
 */

import type { Device, Item, List, SyncDelta } from './types.ts';
import type { ApplyContext, Op } from './ops.ts';
import { applyDeviceOp, applyItemOp, applyListOp, isDeviceOp, isItemOp, isListOp } from './ops.ts';
import { comparePosition } from './position.ts';

export interface HouseholdState {
  lists: Map<string, List>;
  items: Map<string, Item>;
  devices: Map<string, Device>;
  /** Curseur de synchronisation : plus grand updated_at reçu du serveur. */
  cursor: number;
}

export function emptyState(): HouseholdState {
  return { lists: new Map(), items: new Map(), devices: new Map(), cursor: 0 };
}

export function cloneState(state: HouseholdState): HouseholdState {
  return {
    lists: new Map(state.lists),
    items: new Map(state.items),
    devices: new Map(state.devices),
    cursor: state.cursor,
  };
}

/**
 * Intègre un delta serveur dans l'état.
 *
 * Le serveur fait autorité : ses lignes remplacent purement et simplement
 * celles en mémoire. C'est ce qui rend l'opération **idempotente** — appliquer
 * deux fois le même delta donne le même résultat, ce qui autorise le
 * chevauchement d'une milliseconde du curseur (`>=` et non `>`, cf.
 * CLAUDE.md § 3.3).
 */
export function applyDelta(state: HouseholdState, delta: SyncDelta): HouseholdState {
  const next = cloneState(state);

  for (const list of delta.lists) next.lists.set(list.id, list);
  for (const item of delta.items) next.items.set(item.id, item);
  for (const device of delta.devices) next.devices.set(device.id, device);

  // Le curseur ne recule jamais, même si un delta arrive dans le désordre.
  next.cursor = Math.max(next.cursor, delta.cursor);
  return next;
}

/**
 * Applique une opération à un état en mémoire.
 *
 * Utilisé à trois endroits, ce qui garantit qu'ils ne peuvent pas diverger :
 *   - le client, pour l'affichage optimiste ;
 *   - le client, pour rejouer sa file par-dessus l'état serveur ;
 *   - les tests, via le serveur simulé.
 *
 * Renvoie `false` si l'opération n'est pas applicable (ligne inconnue) : le
 * `create` correspondant n'est pas encore passé, l'opération doit être conservée.
 */
export function applyOpToState(state: HouseholdState, op: Op, ctx: ApplyContext): boolean {
  if (isItemOp(op)) {
    const next = applyItemOp(state.items.get(op.id) ?? null, op, ctx);
    if (!next) return false;
    state.items.set(op.id, next);
    return true;
  }

  if (isListOp(op)) {
    const next = applyListOp(state.lists.get(op.id) ?? null, op, ctx);
    if (!next) return false;
    state.lists.set(op.id, next);
    return true;
  }

  if (isDeviceOp(op)) {
    const next = applyDeviceOp(state.devices.get(op.id) ?? null, op, ctx);
    if (!next) return false;
    state.devices.set(op.id, next);
    return true;
  }

  return false;
}

/**
 * Rejoue les opérations en attente par-dessus l'état serveur.
 *
 * Les opérations sont appliquées dans l'ordre de la file (FIFO), avec
 * `now = op.client_ts` : avant acquittement, l'horodatage affiché est celui de
 * l'action réelle sur cet appareil, ce qui est ce que l'utilisateur attend. Il
 * sera remplacé par l'horodatage serveur au prochain delta.
 */
export function project(
  serverState: HouseholdState,
  pendingOps: readonly Op[],
  household_id: string,
): HouseholdState {
  const projected = cloneState(serverState);

  for (const op of pendingOps) {
    applyOpToState(projected, op, { now: op.client_ts, household_id });
  }

  return projected;
}

// ---------------------------------------------------------------------------
// Sélecteurs d'affichage
// ---------------------------------------------------------------------------

export function visibleLists(state: HouseholdState): List[] {
  return [...state.lists.values()]
    .filter((l) => l.deleted_at === null)
    .sort(comparePosition);
}

/**
 * Articles d'une liste, dans l'ordre d'affichage : non cochés d'abord, cochés
 * ensuite (« les articles cochés basculent en bas de liste »).
 */
export function visibleItems(state: HouseholdState, listId: string): Item[] {
  const items = [...state.items.values()].filter(
    (i) => i.list_id === listId && i.deleted_at === null,
  );

  return items.sort((a, b) => {
    if (a.is_checked !== b.is_checked) return a.is_checked ? 1 : -1;
    return comparePosition(a, b);
  });
}

/**
 * Suggestions à la frappe, construites à partir des articles déjà saisis dans
 * cette liste — y compris supprimés et cochés, puisque l'intérêt est justement
 * de réajouter ce qu'on achète régulièrement.
 *
 * Entièrement local : aucune requête réseau, donc aucun coût de quota et un
 * fonctionnement identique hors ligne.
 */
export function suggestItems(
  state: HouseholdState,
  listId: string,
  query: string,
  limit = 6,
): { label: string; quantity: string | null }[] {
  const needle = normalise(query);
  if (needle.length === 0) return [];

  // Libellés déjà présents et actifs dans la liste : les proposer ferait
  // doublon.
  const present = new Set(
    [...state.items.values()]
      .filter((i) => i.list_id === listId && i.deleted_at === null)
      .map((i) => normalise(i.label)),
  );

  const seen = new Map<string, { label: string; quantity: string | null; at: number }>();

  for (const item of state.items.values()) {
    if (item.list_id !== listId) continue;

    const key = normalise(item.label);
    if (key.length === 0 || present.has(key)) continue;
    if (!key.startsWith(needle) && !key.includes(` ${needle}`)) continue;

    const previous = seen.get(key);
    // On garde l'occurrence la plus récente : la quantité proposée est celle de
    // la dernière fois qu'on a acheté cet article.
    if (!previous || item.created_at > previous.at) {
      seen.set(key, { label: item.label, quantity: item.quantity, at: item.created_at });
    }
  }

  return [...seen.values()]
    .sort((a, b) => b.at - a.at)
    .slice(0, limit)
    .map(({ label, quantity }) => ({ label, quantity }));
}

/** Minuscules sans accents, pour comparer « Œufs » et « oeufs ». */
export function normalise(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}
